import { format, isBefore } from 'date-fns'
import { Customer } from '../../contacts/models/customer'
import { EndUser } from '../../contacts/models/end-user'
import { Updatable } from '../../core/models/updatable'
import { Version } from '../../core/models/version'
import { Category } from '../../organisation/models/category'
import { Engineer } from '../../organisation/models/engineer'
import { alias, AliasedConstructor } from '../../rest/decorators/alias'
import { Mappable, Mapped } from '../../rest/decorators/mapped'
import { RestQuery } from '../../rest/utilities/rest-query'
import {
  DynamicBinding,
  SerialisationConfig,
} from '../../rest/utilities/serialisable'
import { JobStatus } from './job-status'

@Mapped({})
class Quote extends Mappable {
  id!: number
  reference!: string

  static create<Data>(attrs: Data) {
    return new Quote().map(attrs)
  }
}

@Mapped(
  {
    quote: { format: Quote.create },
  },
  ['numericID']
)
class QuoteRevision extends Mappable {
  id!: number
  numericID!: number
  ulid!: number
  reference!: string
  quote!: Quote | null

  static create<Data>(attrs: Data) {
    return new QuoteRevision().map(attrs)
  }
}

@Mapped(
  {
    internal_id: { property: 'internalID' },
  },
  ['numericID']
)
class Job extends Mappable {
  id!: string | number
  numericID!: number
  ulid!: string
  internalID!: string

  static create<Data>(attrs: Data) {
    return new Job().map(attrs)
  }
}

@Mapped(
  {
    date: { type: 'date' },
    start_at: { property: 'start', type: 'date' },
    end_at: { property: 'end', type: 'date' },
    job: { format: Job.create },
    engineer: {
      format: Engineer.create,
    },
    internal_status: {
      property: 'internalStatus',
      format: JobStatus.create,
    },
    hq_workshop: { property: 'hqWorkshop' },
    midlands_workshop: { property: 'midlandsWorkshop' },
  },
  ['numericID']
)
export class OrderedAllocation extends Mappable {
  id!: string | number
  numericID!: number
  date!: Date
  start!: Date
  end!: Date
  job?: Job
  engineer?: Engineer
  internalStatus?: JobStatus
  hqWorkshop!: boolean
  midlandsWorkshop!: boolean

  static create<Data>(attrs: Data) {
    return new OrderedAllocation().map(attrs)
  }
}

export enum JobTypeFieldCategory {
  standard = 'standard',
  van = 'van',
}

export type JobTypeFieldKey =
  | 'typeApproval'
  | 'welfareBuild'
  | 'welfareSale'
  | 'baseVanSale'
  | 'turnKeySolutionsSale'
  | 'turnKeySolutionsBuild'
  | 'fordCustomSale'
  | 'fordCustomBuild'
  | 'install'
  | 'serviceCall'
  | 'vanConversion'
  | 'kitSupply'
  | 'stockCarriedOver'
  | 'rnd'
  | 'apache'
  | 'stream'

export interface JobTypeField {
  key: JobTypeFieldKey
  title: string
  value: string
  category: JobTypeFieldCategory
}

export class SalesPurchaseOrder extends Updatable {
  public static override serialisationConfig: SerialisationConfig =
    new SerialisationConfig(() => new SalesPurchaseOrder(), {
      revision: new SerialisationConfig(() => new QuoteRevision()),
      category: new SerialisationConfig(() => new Category()),
      customer: Customer.serialisationConfig,
      endUser: EndUser.serialisationConfig,
      allocations: new SerialisationConfig(() => new OrderedAllocation()),
      firstVersion: new SerialisationConfig(() => new Version()),
      lastVersion: new SerialisationConfig(() => new Version()),
      currentVersion: new SerialisationConfig(() => new Version()),
    })
  override mandatory = ['id']

  @alias('id')
  id!: number
  @alias('numeric_id')
  numericID!: number
  @alias('ulid')
  ulid!: number
  @alias('revision')
  revision!: QuoteRevision | null
  @alias('po_number')
  poNumber!: string
  @alias('is_payment_required')
  paymentRequired!: boolean
  @alias('admin_notes')
  adminNotes!: string
  @alias('type_approval')
  typeApproval!: boolean
  @alias('welfare')
  welfareBuild!: boolean
  @alias('welfare_sale')
  welfareSale!: boolean
  @alias('van_sale')
  baseVanSale!: boolean
  @alias('turn_key_solutions_sale')
  turnKeySolutionsSale!: boolean
  @alias('van_sale_extras')
  turnKeySolutionsBuild!: boolean
  @alias('ford_custom_sale')
  fordCustomSale!: boolean
  @alias('ford_custom_build')
  fordCustomBuild!: boolean
  @alias('install')
  install!: boolean
  @alias('service_call')
  serviceCall!: boolean
  @alias('van_conversion')
  vanConversion!: boolean
  @alias('kit_supply')
  kitSupply!: boolean
  @alias('stock_carried_over')
  stockCarriedOver!: boolean
  @alias('r_n_d')
  rnd!: boolean
  @alias('apache')
  apache!: boolean
  @alias('stream')
  stream!: boolean
  @alias('date')
  date!: Date | null
  @alias('price_code')
  priceCode!: string
  @alias('total_value')
  totalValue!: number | null
  @alias('address')
  address!: string
  @alias('postcode')
  postcode!: string
  @alias('fitting_on')
  fittingDate!: Date | null
  @alias('fitting_notes')
  fittingNotes!: string
  @alias('parts_comments')
  partsComments!: string
  @alias('work_required')
  workRequired!: string
  @alias('comments')
  comments!: string
  @alias('internal_comments')
  internalComments!: string
  @alias('category')
  category!: Category | null
  @alias('customer')
  customer!: Customer | null
  @alias('end_user')
  endUser!: EndUser | null
  @alias('sales_person')
  salesPerson!: string
  @alias('registration')
  registration!: string
  @alias('chassis_number')
  chassisNumber!: string
  @alias('notes')
  notes!: string
  @alias('days')
  days!: number | null
  @alias('hours')
  hours!: number | null
  @alias('allocations')
  allocations!: OrderedAllocation[]
  @alias('status')
  status!: string
  @alias('estimating_at')
  estimating: Date | null = null
  @alias('estimated_at')
  estimated: Date | null = null
  @alias('booking_at')
  booking: Date | null = null
  @alias('booked_at')
  booked: Date | null = null
  @alias('offered_at')
  offered: Date | null = null
  @alias('confirmed_at')
  confirmed: Date | null = null
  @alias('cancelled_at')
  cancelled: Date | null = null
  @alias('created_at')
  created: Date | null = null
  @alias('archived_at')
  archived: Date | null = null
  @alias('deleted')
  deleted!: boolean
  @alias('first_version')
  firstVersion!: Version | null
  @alias('last_version')
  lastVersion!: Version | null
  @alias('current_version')
  currentVersion!: Version | null
  @alias('author')
  author!: string
  @alias('last_edited')
  lastEdited!: Date

  public static typeFields: JobTypeField[] = [
    {
      key: 'typeApproval',
      title: 'Type Approval',
      value: 'T',
      category: JobTypeFieldCategory.standard,
    },
    {
      key: 'install',
      title: 'Install',
      value: 'I',
      category: JobTypeFieldCategory.standard,
    },
    {
      key: 'serviceCall',
      title: 'Service Call',
      value: 'SC',
      category: JobTypeFieldCategory.standard,
    },
    {
      key: 'vanConversion',
      title: 'Van Conversion',
      value: 'VC',
      category: JobTypeFieldCategory.standard,
    },
    {
      key: 'kitSupply',
      title: 'Kit Supply',
      value: 'K',
      category: JobTypeFieldCategory.standard,
    },
    {
      key: 'stockCarriedOver',
      title: 'Stock Carried Over',
      value: 'SCO',
      category: JobTypeFieldCategory.standard,
    },
    {
      key: 'rnd',
      title: 'Research and Development',
      value: 'RND',
      category: JobTypeFieldCategory.standard,
    },
    {
      key: 'apache',
      title: 'Apache',
      value: 'A',
      category: JobTypeFieldCategory.standard,
    },
    {
      key: 'stream',
      title: 'Stream',
      value: 'ST',
      category: JobTypeFieldCategory.standard,
    },
    {
      key: 'welfareSale',
      title: 'Welfare Sale',
      value: 'WS',
      category: JobTypeFieldCategory.van,
    },
    {
      key: 'welfareBuild',
      title: 'Welfare Build',
      value: 'WB',
      category: JobTypeFieldCategory.van,
    },
    {
      key: 'baseVanSale',
      title: 'Base Van Sale',
      value: 'BVS',
      category: JobTypeFieldCategory.van,
    },
    {
      key: 'turnKeySolutionsSale',
      title: 'Turn Key Solutions Sale',
      value: 'TKS',
      category: JobTypeFieldCategory.van,
    },
    {
      key: 'turnKeySolutionsBuild',
      title: 'Turn Key Solutions Build',
      value: 'TKB',
      category: JobTypeFieldCategory.van,
    },
    {
      key: 'fordCustomSale',
      title: 'Ford Custom Sale',
      value: 'FCS',
      category: JobTypeFieldCategory.van,
    },
    {
      key: 'fordCustomBuild',
      title: 'Ford Custom Build',
      value: 'FCB',
      category: JobTypeFieldCategory.van,
    },
  ]

  constructor() {
    super((SalesPurchaseOrder as unknown as AliasedConstructor)._alias)
  }

  get identity(): string {
    const parts = []
    if (this.registration) {
      parts.push(this.registration)
    }
    if (this.chassisNumber) {
      parts.push(this.chassisNumber)
    }
    return parts.join(' / ')
  }

  get summary(): string {
    const parts = [this.poNumber, this.identity]
    if (this.postcode) {
      parts.push(this.postcode)
    }
    if (this.customer) {
      parts.push(this.customer.name)
    }
    return parts.join(' | ')
  }

  get statusCssClass(): string {
    return this.status
      ? `po-${this.status
          .toLowerCase()
          .replace(/[^a-z0-9\s!?]/g, '')
          .replace(/[^a-z0-9!?]/g, '-')}`
      : ''
  }

  get totalHours(): number {
    return (this.days ?? 0) * 8 + (this.hours ?? 0)
  }

  get jobs(): string {
    const jobs: string[] = []
    if (this.allocations && this.allocations.length > 0) {
      this.allocations.forEach((allocation) => {
        if (
          allocation.job &&
          jobs.filter((j) => j === allocation.job?.internalID).length < 1
        ) {
          jobs.push(allocation.job.internalID)
        }
      })
    }
    return jobs.join(', ')
  }

  get bookingDate(): Date | null {
    const date = null
    if (this.allocations && this.allocations.length > 0) {
      return this.allocations.sort((a1, a2) =>
        isBefore(a1.date, a2.date) ? 1 : -1
      )[0].date
    }
    return date
  }

  override serialise(): object {
    const data = super.serialise() as DynamicBinding
    if (data['date']) {
      data['date'] = this.date ? format(this.date, 'yyyy-MM-dd') : null
    }
    if (data['fitting_on']) {
      data['fitting_on'] = this.fittingDate
        ? format(this.fittingDate, 'yyyy-MM-dd')
        : null
    }
    if (Object.prototype.hasOwnProperty.call(data, 'category')) {
      data['category_id'] = this.category?.id ?? null
      delete data['category']
    }
    if (Object.prototype.hasOwnProperty.call(data, 'customer')) {
      data['customer_id'] = this.customer?.id ?? null
      delete data['customer']
    }
    if (Object.prototype.hasOwnProperty.call(data, 'end_user')) {
      data['end_user_id'] = this.endUser?.id ?? null
      delete data['end_user']
    }
    if (data['allocations']) {
      delete data['allocations']
    }
    if (data['status']) {
      delete data['status']
    }
    if (data['created']) {
      delete data['created']
    }
    if (data['first_version']) {
      delete data['first_version']
    }
    if (data['last_version']) {
      delete data['last_version']
    }
    if (data['current_version']) {
      delete data['current_version']
    }
    return data
  }

  static create<Data>(attrs: Data) {
    return new SalesPurchaseOrder().update(
      attrs,
      SalesPurchaseOrder.serialisationConfig
    )
  }
}

interface Filters {
  keywords?: string
  month?: {
    month: string
    year: string
  }
  range?: {
    start: string
    end: string
  }
  status?: string
  booking?: boolean
  archive?: boolean
}
type Ordering =
  | 'po_number'
  | '-po_number'
  | 'date'
  | '-date'
  | 'customer'
  | '-customer'
  | 'end_user'
  | '-end_user'
  | 'created_at'
  | '-created_at'
  | 'status'
  | '-status'
type Includes =
  | 'customer'
  | 'endUser'
  | 'allocations'
  | 'allocations.job'
  | 'allocations.engineer'
  | 'revision'
  | 'revision.quote'
  | 'revision.quote.salesPerson'

export class SalesPurchaseOrderQuery extends RestQuery<
  Filters,
  Ordering,
  Includes
> {}
